import API_BASE_URL from 'src/constants';
import { getToken, handleUnauthorized } from './authToken';

export const BUG_CATEGORIES = [
  'dashboard',
  'inventory',
  'inbound',
  'outbound',
  'returns',
  'forecasts',
  'contact',
  'cases',
  'claims',
  'ai_chat',
  'advanced_reports',
  'statistics',
  'carrier_dashboard',
  'countries',
  'carrier_list',
  'workflows',
  'integrations',
  'settings',
  'other',
] as const;
export type BugCategory = (typeof BUG_CATEGORIES)[number];

export const BUG_SEVERITIES = ['blockiert', 'behindert', 'stoert'] as const;
export type BugSeverity = (typeof BUG_SEVERITIES)[number];

const ROUTE_CATEGORY: [string, BugCategory][] = [
  ['/warehouse/inventory', 'inventory'],
  ['/warehouse/inbound', 'inbound'],
  ['/outbound', 'outbound'],
  ['/returns', 'returns'],
  ['/forecasts', 'forecasts'],
  ['/contact', 'contact'],
  ['/cases/claims', 'claims'],
  ['/cases', 'cases'],
  ['/ai-chat', 'ai_chat'],
  ['/advanced-reports', 'advanced_reports'],
  ['/statistics', 'statistics'],
  ['/carrier-analytics/dashboard', 'carrier_dashboard'],
  ['/carrier-analytics/countries', 'countries'],
  ['/carrier-analytics/carrier-list', 'carrier_list'],
  ['/workflows', 'workflows'],
  ['/integrations', 'integrations'],
  ['/settings', 'settings'],
  ['/dashboard', 'dashboard'],
];

export const categoryForPath = (pathname: string): BugCategory =>
  ROUTE_CATEGORY.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'other';

export const MAX_ATTACHMENTS = 10;
export const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.pdf,.docx,.mp4,.mov';
export const ATTACHMENT_MIMETYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};
export const SCREENSHOT_MAX_WIDTH = 1600;

/** Mimetype from the extension: browsers leave file.type empty for some types (e.g. .mov on Windows). */
export const mimetypeForFile = (file: File): string | null => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ATTACHMENT_MIMETYPES[extension] ?? null;
};

export const fileToBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** The current page as PNG (base64), the way the intake wants screenshots. */
export const captureScreenshot = async (): Promise<string | null> => {
  try {
    const { toPng } = await import('html-to-image');
    const dataUrl = await toPng(document.documentElement, {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: Math.min(1, SCREENSHOT_MAX_WIDTH / window.innerWidth),
      filter: node => node.nodeName !== 'NOSCRIPT' && (node as HTMLElement).id !== 'portal-root',
    });
    return dataUrl.split(',')[1];
  } catch {
    return null;
  }
};

export const base64Bytes = (data: string): number => Math.floor((data.length * 3) / 4);

export interface BugReportPayload {
  title: string;
  category: BugCategory;
  where_happened: string;
  happened_at: string;
  expected_result: string;
  actual_result: string;
  preceding_actions: string;
  severity: BugSeverity;
  app_version: string;
  attachments: { name: string; mimetype: string; file: string }[];
}

export interface BugReportResult {
  id: number;
  name: string;
  forward_state: 'disabled' | 'pending' | 'sent' | 'failed';
  remote_number: number | null;
  remote_url: string | null;
  message: string | null;
  rejected_attachments: { name: string; reason: string }[];
}

export async function sendBugReport(payload: BugReportPayload): Promise<BugReportResult> {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}portal/bug_reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (response.status === 401) handleUnauthorized(token);
  if (!response.ok) {
    let detail = `Bug report failed (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: string };
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch {
      // keep generic message
    }
    throw new Error(detail);
  }
  return (await response.json()) as BugReportResult;
}
